import React from 'react';
import { TExtraRate } from './types';
import { isValidNumber } from './utils';
import { CardHeader } from './shared/CardHeader';
import { EditableField } from './shared/EditableField';

interface ExtraRateCardProps {
    rate: TExtraRate;
    isEditing: boolean;
    onUpdateRate: (
        rateId: number,
        updater: (rate: TExtraRate) => TExtraRate,
    ) => void;
}

export const ExtraRateCard: React.FC<ExtraRateCardProps> = React.memo(({
    rate,
    isEditing,
    onUpdateRate,
}) => {
    const handleDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        onUpdateRate(rate.id, current => ({
            ...current,
            duration: Number.isNaN(value) ? 0 : value,
        }));
    };

    const handleFreelancerPayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        onUpdateRate(rate.id, current => ({
            ...current,
            freelancerPayPerHour: Number.isNaN(value) ? 0 : value,
        }));
    };

    const handleClientPayChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = parseFloat(e.target.value);
        onUpdateRate(rate.id, current => ({
            ...current,
            clientPayPerHour: Number.isNaN(value) ? 0 : value,
        }));
    };

    const handleDescriptionChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        onUpdateRate(rate.id, current => ({
            ...current,
            description: e.target.value,
        }));
    };

    const hasDurationError = !isValidNumber(rate.duration);
    const hasFreelancerPayError = !isValidNumber(rate.freelancerPayPerHour);
    const hasClientPayError = !isValidNumber(rate.clientPayPerHour);
    const hasDescriptionError = !rate.description?.trim();

    return (
        <div className="timesheet-booking-card" key={`rate-${rate.id}`}>
            <CardHeader
                pills={[
                    { label: 'Job Ref', value: rate.jobReference },
                    { label: 'Rate Code', value: rate.extraRateCode },
                ]}
            />
            <div className="timesheet-booking-card__body">
                <table className="timesheet-booking-table">
                    <tbody>
                        <EditableField
                            label="Duration (hrs)"
                            value={rate.duration}
                            isEditing={isEditing}
                            hasError={hasDurationError}
                            fieldType="number"
                            onChange={handleDurationChange}
                            ariaLabel={`Duration for rate ${rate.extraRateCode}`}
                        />
                        <EditableField
                            label="Hourly Rate"
                            value={rate.freelancerPayPerHour}
                            isEditing={isEditing}
                            hasError={hasFreelancerPayError}
                            fieldType="currency"
                            onChange={handleFreelancerPayChange}
                            ariaLabel={`Hourly rate for ${rate.extraRateCode}`}
                        />
                        <EditableField
                            label="Client Rate"
                            value={rate.clientPayPerHour}
                            isEditing={isEditing}
                            hasError={hasClientPayError}
                            fieldType="currency"
                            onChange={handleClientPayChange}
                            ariaLabel={`Client rate for ${rate.extraRateCode}`}
                        />
                        <EditableField
                            label="Description"
                            value={rate.description}
                            isEditing={isEditing}
                            hasError={hasDescriptionError}
                            fieldType="textarea"
                            onChange={handleDescriptionChange}
                            ariaLabel={`Description for ${rate.extraRateCode}`}
                        />
                    </tbody>
                </table>
            </div>
        </div>
    );
});

ExtraRateCard.displayName = 'ExtraRateCard';

